import { useTranslation } from 'react-i18next';
import {
  Menu,
  ShoppingCart,
  Wallet as WalletIcon,
  Bell,
  LogOut,
  Presentation,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useDataService } from '@/lib/service-context';
import { useProAuth } from '@/lib/auth-context';
import { useProBranding } from '@/lib/pro-branding';
import { useCart } from '@/lib/cart-context';
import { useFlyToCart } from '@/lib/fly-to-cart';
import { formatTND } from '@/lib/format';
import { CLIENT_MODE_ROUTES } from '@/lib/client-mode';
import { getPageMeta } from '@/lib/page-meta';
import { NotificationPanel } from '@/components/notifications/NotificationPanel';

/**
 * En-tête de l'espace électricien.
 *
 * Deux flancs gauches selon la largeur :
 *  - `≥ lg` → titre de page + fil d'Ariane, INCHANGÉ ;
 *  - `< lg` → le logo, et le repère de page descend en tête du contenu
 *    (`PageHeading`). Sur un téléphone, titre et fil ne tenaient pas à côté
 *    des quatre actions : le fil se tronquait et le titre collait aux icônes.
 *
 * Le logo ne monte pas au-delà de `lg` : la barre latérale est alors visible
 * et le porte déjà.
 */
export function ElectricianHeader({
  onToggleSidebar,
}: {
  onToggleSidebar: () => void;
}) {
  const { t } = useTranslation();
  const ds = useDataService();
  const { proCustomer, logout } = useProAuth();
  const { loginLogoUrl } = useProBranding();
  const { itemCount, setDrawerOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  // Repère de page — rendu ici seulement à partir de `lg`. Hors table (la
  // formation, la planche des notifications), on garde le repli historique.
  const pageMeta = getPageMeta(location.pathname);
  const title = pageMeta ? t(pageMeta.title.key, pageMeta.title.fallback) : 'Tunelec Pro';
  const crumb = pageMeta?.parents.length
    ? [...pageMeta.parents, pageMeta.title]
        .map((c) => t(c.key, c.fallback))
        .join(' / ')
    : '';

  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);
  const [showNotifMenu, setShowNotifMenu] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);

  const cartButtonRef = useRef<HTMLButtonElement>(null);
  const { setCartTargetRef, bumpKey } = useFlyToCart();

  useEffect(() => {
    setCartTargetRef(cartButtonRef);
  }, [setCartTargetRef]);

  useEffect(() => {
    if (!showAvatarMenu) return;
    const handler = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setShowAvatarMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAvatarMenu]);

  useEffect(() => {
    if (!showNotifMenu) return;
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifMenu(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowNotifMenu(false);
    };
    // `pointerdown` et non `mousedown` : sur iOS, un tap sur un élément non
    // interactif ne remonte pas toujours d'événement souris jusqu'au document,
    // et le panneau restait ouvert au doigt.
    document.addEventListener('pointerdown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [showNotifMenu]);

  const walletQuery = useQuery({
    queryKey: ['pro-wallet-balance'],
    queryFn: () => ds.proWallet.getBalance(),
    enabled: !!proCustomer,
    refetchInterval: 60_000,
  });

  // Real notifications feed (backed by the pro_notifications table).
  // Triggers on pro_devis / pro_commandes / pro_wallet_movements push
  // rows here. We poll every 2 minutes — no Supabase Realtime per
  // CLAUDE.md Phase 1-2 rule.
  const qc = useQueryClient();
  const notificationsQuery = useQuery({
    queryKey: ['pro-notifications', 'recent'],
    queryFn: () => ds.proNotifications.list({ limit: 30 }),
    enabled: !!proCustomer,
    refetchInterval: 120_000,
  });
  const notifications = notificationsQuery.data ?? [];

  // Le compteur vient du serveur, pas du décompte de la page chargée :
  // au-delà de la limite, compter côté client sous-évaluait le badge et
  // faisait disparaître « Tout lire » alors qu'il restait des non-lues.
  const unreadQuery = useQuery({
    queryKey: ['pro-notifications', 'unread-count'],
    queryFn: () => ds.proNotifications.unreadCount(),
    enabled: !!proCustomer,
    refetchInterval: 120_000,
  });
  const unreadCount = unreadQuery.data ?? 0;

  const markRead = useMutation({
    mutationFn: (id: string) => ds.proNotifications.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pro-notifications'] }),
  });
  const markAllRead = useMutation({
    mutationFn: () => ds.proNotifications.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pro-notifications'] }),
  });

  // Une seule référence d'« instant » pour tout le panneau : sinon chaque ligne
  // construirait son propre `new Date()` et deux notifications de la même
  // seconde pourraient tomber dans deux groupes de jour différents.
  const [panelNow, setPanelNow] = useState(() => new Date());

  const initials = (() => {
    const f = (proCustomer?.firstName ?? '').charAt(0).toUpperCase();
    const l = (proCustomer?.lastName ?? '').charAt(0).toUpperCase();
    return f + l || 'TP';
  })();

  return (
    <header
      // `relative` sert d'ancre au panneau de notifications : sur mobile il
      // s'aligne sur la LARGEUR DE L'ÉCRAN (via l'en-tête), pas sur la cloche —
      // ancré sur la cloche, un panneau de 380 px débordait par la gauche.
      className="relative px-4 sm:px-6 py-4 lg:py-5 flex items-center justify-between gap-2 sm:gap-4"
      style={{ background: 'var(--pro-bg)' }}
    >
      <div className="flex items-center gap-2 sm:gap-3 min-w-0">
        <button
          type="button"
          aria-label={t('pro.header.toggle_sidebar', 'Menu')}
          onClick={onToggleSidebar}
          className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-white/60"
        >
          <Menu className="w-5 h-5" />
        </button>
        {/* Mobile — le logo prend la place du repère de page.
            Largeur EXPLICITE sur le conteneur : le preflight Tailwind pose
            `img { max-width: 100% }`, et dans un élément flex dimensionné par
            son contenu la contrainte devient circulaire (l'image se replie à
            0×0). La barre latérale y échappe grâce à sa largeur fixe. */}
        <Link
          to="/tableau-de-bord"
          className="lg:hidden flex items-center min-w-0"
          aria-label="Tunielec Pro"
        >
          <div className="w-[112px] sm:w-[150px] min-w-0">
            {loginLogoUrl ? (
              <img
                src={loginLogoUrl}
                alt=""
                className="max-h-9 max-w-full w-auto object-contain object-left"
              />
            ) : (
              <div className="flex items-baseline gap-1">
                <span
                  className="text-[20px] font-bold leading-none"
                  style={{ fontFamily: 'Figtree', color: 'var(--pro-text-dark)' }}
                >
                  Tunelec
                </span>
                <span
                  className="text-[20px] font-semibold italic leading-none"
                  style={{ fontFamily: 'Figtree', color: 'var(--pro-primary)' }}
                >
                  Pro
                </span>
              </div>
            )}
          </div>
        </Link>

        {/* Desktop — titre + fil d'Ariane, inchangés. */}
        <div className="hidden lg:block min-w-0">
          <h1
            className="text-[24px] font-semibold leading-tight truncate"
            style={{ fontFamily: 'Figtree', color: 'var(--pro-text-dark)' }}
          >
            {title}
          </h1>
          {crumb && (
            <p
              className="text-[13px] mt-0.5 truncate"
              style={{ fontFamily: 'Figtree', color: 'var(--pro-primary)' }}
            >
              {crumb}
            </p>
          )}
        </div>
      </div>

      {/* `shrink-0` : les actions gardent leur taille, c'est le logo qui se
          resserre sur les écrans les plus étroits — jamais l'inverse. */}
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        {/* Mode client — retourne l'écran vers le client de l'électricien */}
        <Link
          to={CLIENT_MODE_ROUTES.shop}
          aria-label={t('pro.client_mode.enter', 'Mode client')}
          title={t('pro.client_mode.enter', 'Mode client')}
          className="w-10 h-10 rounded-xl bg-white flex items-center justify-center hover:bg-neutral-50 transition-colors"
        >
          <Presentation
            className="w-5 h-5"
            style={{ color: 'var(--pro-text-dark)' }}
          />
        </Link>

        {/* Wallet pill */}
        <Link
          to="/portefeuille"
          className="hidden sm:inline-flex items-center gap-2 h-10 px-4 rounded-full bg-white text-[14px] font-semibold transition-colors"
          style={{
            border: '2px solid var(--pro-primary)',
            color: 'var(--pro-text-dark)',
            fontFamily: 'Figtree',
          }}
        >
          <WalletIcon
            className="w-4 h-4"
            style={{ color: 'var(--pro-primary)' }}
          />
          {formatTND(walletQuery.data?.balance ?? 0)}
        </Link>

        {/* Cart */}
        <button
          ref={cartButtonRef}
          type="button"
          aria-label={t('pro.header.cart', 'Panier')}
          onClick={() => setDrawerOpen(true)}
          className="relative w-10 h-10 rounded-xl bg-white flex items-center justify-center hover:bg-neutral-50 transition-colors"
        >
          <ShoppingCart
            className="w-5 h-5"
            style={{ color: 'var(--pro-text-dark)' }}
          />
          {itemCount > 0 && (
            <span
              key={bumpKey}
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white pro-bump"
              style={{ background: 'var(--pro-primary)' }}
            >
              {itemCount}
            </span>
          )}
        </button>

        {/* Notifications */}
        {/* Statique sous `sm` : le panneau se positionne alors par rapport à
            l'en-tête (pleine largeur) et non par rapport au bouton. */}
        <div className="sm:relative" ref={notifRef}>
          <button
            type="button"
            aria-label={t('pro.header.notifications', 'Notifications')}
            onClick={() => {
              // On refige l'instant à chaque ouverture : un onglet resté
              // ouvert la nuit afficherait sinon « aujourd'hui » pour hier.
              if (!showNotifMenu) setPanelNow(new Date());
              setShowNotifMenu((v) => !v);
            }}
            aria-expanded={showNotifMenu}
            className="relative w-10 h-10 rounded-xl bg-white flex items-center justify-center hover:bg-neutral-50 transition-colors"
          >
            <Bell
              className="w-5 h-5"
              style={{ color: 'var(--pro-text-dark)' }}
            />
            {unreadCount > 0 && (
              <span
                key={unreadCount}
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white pro-bump"
                style={{ background: 'var(--pro-primary)' }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {showNotifMenu && (
            <div
              className={[
                // Mobile : bandeau pleine largeur collé sous l'en-tête.
                'absolute inset-x-4 top-full mt-1 origin-top',
                // ≥ sm : menu ancré sous la cloche, largeur fixe.
                'sm:inset-x-auto sm:right-0 sm:rtl:right-auto sm:rtl:left-0 sm:top-12 sm:mt-0 sm:w-[380px] sm:origin-top-right',
                'rounded-2xl bg-white shadow-xl overflow-hidden z-50 pro-scale-in',
              ].join(' ')}
              style={{ border: '1px solid var(--pro-border)' }}
              role="dialog"
              aria-label={t('pro.header.notifications', 'Notifications')}
            >
              <NotificationPanel
                notifications={notifications}
                unreadCount={unreadCount}
                isLoading={notificationsQuery.isLoading}
                now={panelNow}
                // Le panneau ne doit jamais dépasser du bas de l'écran : sur un
                // téléphone en paysage, 420 px de liste sortaient du viewport.
                maxHeight="min(420px, 60vh)"
                onMarkAllRead={() => markAllRead.mutate()}
                isMarkingAllRead={markAllRead.isPending}
                onSelect={(n) => {
                  if (n.readAt === null) markRead.mutate(n.id);
                  setShowNotifMenu(false);
                  if (n.linkUrl) navigate(n.linkUrl);
                }}
              />
            </div>
          )}
        </div>

        {/* Avatar */}
        <div className="relative" ref={avatarRef}>
          <button
            type="button"
            onClick={() => setShowAvatarMenu((v) => !v)}
            className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-[14px] font-semibold transition-opacity hover:opacity-90 overflow-hidden"
            style={{ background: 'var(--pro-primary)', fontFamily: 'Figtree' }}
            aria-label={t('pro.header.profile', 'Mon compte')}
          >
            {proCustomer?.profilePictureUrl ? (
              <img
                src={proCustomer.profilePictureUrl}
                alt={initials}
                className="w-full h-full object-cover"
              />
            ) : (
              initials
            )}
          </button>
          {showAvatarMenu && (
            <div
              className="absolute right-0 top-12 w-56 rounded-xl bg-white shadow-lg overflow-hidden z-50 pro-scale-in"
              style={{
                border: '1px solid var(--pro-border)',
                transformOrigin: 'top right',
              }}
            >
              <div
                className="px-4 py-3"
                style={{ borderBottom: '1px solid var(--pro-line)' }}
              >
                <div
                  className="text-[14px] font-semibold"
                  style={{ color: 'var(--pro-text-dark)' }}
                >
                  {proCustomer?.firstName} {proCustomer?.lastName}
                </div>
                <div
                  className="text-[12px] truncate"
                  style={{ color: 'var(--pro-text-muted)' }}
                >
                  {proCustomer?.email}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowAvatarMenu(false);
                  navigate('/settings');
                }}
                className="w-full text-left px-4 py-2.5 text-[13px] hover:bg-neutral-50"
                style={{ color: 'var(--pro-text-dark)' }}
              >
                {t('pro.header.settings_link', 'Paramètres')}
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowAvatarMenu(false);
                  await logout();
                  navigate('/login');
                }}
                className="w-full flex items-center gap-2 text-left px-4 py-2.5 text-[13px] hover:bg-red-50"
                style={{ color: 'var(--pro-danger)' }}
              >
                <LogOut className="w-4 h-4" />
                {t('pro.header.logout', 'Déconnexion')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
